import React, { useState, useEffect } from "react";
import FilterStage from "./filteringStage";
import ImportingStage from "./importingStage";
import MappingStage from "./mappingStage";
import RenderingStage from "./renderingStage";
import InsightStage from "./insightStage";
import axios from "axios";
import { Offcanvas, Button } from "react-bootstrap";
import { createTheme } from "@mui/material/styles";

export default function MainPage() {
  const [metaData, setMetaData] = useState([]);
  const [heatData, setHeatData] = useState([[],[]]);
  const [showSideBar, setShowSideBar] = useState(false);
  const [sideBarTitle, setSideBarTitle] = useState("");
  const [sideBarBody, setSideBarBody] = useState("");
  const [strokeWidth, setStrokeWidth] = useState(1);
  const [domain, setDomain] = useState([0,100]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [chartType, setChartType] = useState("Line");
  const [dataSetsDomain, setDataSetsDomain] = useState(["auto","auto"]);
  const [showDialog, setShowDialog] = useState(true);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URI
  });

  // Button / slider theme
  const theme = createTheme({
    palette: {
      primary: {
        main: "#AA5656"
      },
      secondary: {
        main: "#698269"
      },
      main: {
        main: "#628EA6"
      }
    }
  });

  // Possible texts for in the side bar, sorted by the stage it belongs to
  const sideBarData = {
    "measuringStage": {
      title: "Measuring stage",
      body: "In the measuring stage, you measure what you want to know in whatever way fits your purpose. In this case, 88 people living throughout Groningen placed heat sensors in their homes to measure the temperature once every 30 minutes, over the course of several months!"
    },
    "importingStage": {
      title: "Importing stage",
      body: "The data produced by the measuring devices is not readable nor usable by humans yet. Therefore, this data is first imported into a dataset that *is* usable, such as a CSV file. For the graphs on this website, 1.5 megabytes of data from CSV files was imported by researchers from Hanzehogeschool Groningen. To organize all this data and save some information about how it was obtained, metadata is used. Some of this metadata is shown in the importing box."
    },
    "filteringStage": {
      title: "Filtering stage",
      body: "The table shows some possible temperatures at certain time stamps. Can you see anything to be concluded from the temperatures? Probably not. This is why we visualize data.\n\nOnce imported, the stored values can have lots of properties that make them unfit for visualizing. Think of sudden very high or very low values, incorrect measurements or \"noise\". Because of this, the data is put through transformations, performed either by a computer or by hand, that make them well-fit for visualizations. These transformations can also include adding data in cases where the visuals are more important than how true they are to reality."
    },
    "mappingStage": {
      title: "Mapping stage",
      body: "The filtered dataset is what is sent to your computer when you visit this website. Once here, properties are added to the data that contain information on where and how they should be located in the visualization. This is called a mapping. In this case, the added properties are things like the locations of the points that a line graph runs through, how those points should be connected and the thickness of the line."
    },
    "renderingStage": {
      title: "Rendering stage",
      body: "Finally, your computer has enough information to draw the visualization on the screen. It does this by calculating, pixel by pixel, what colour it should have and sending a signal to the display correspondingly. Every time you decide to switch between visualizations or datasets (using the dropdown menus on the main page), the final three steps are performed once more to make a new graph to explore and have fun with!"
    },
    "insightStage": {
      title: "Insight",
      body: "The ultimate goal of visualizing data is to gain some insight into the data. This section challenges you to answer some questions based on the visualizations!"
    }
  }

  // Open the sidebar corresponding to a section named in the sideBarData dictionary
  function openSideBar(section) {
    setSideBarTitle(sideBarData[section].title);
    setSideBarBody(sideBarData[section].body);
    setShowSideBar(true);
  }

  const closeSideBar = () => setShowSideBar(false);

  // Return the minimum datetime as an integer in milliseconds in the domain of a dataset
  function getDomainMin(newHeatData) {
    let domainMin = Number.MAX_VALUE;
    let firstDateTime = Number.MAX_VALUE;
    newHeatData.forEach(set => {
      if(set.length > 0){
        const targetDate = new Date("2021-05-01").getTime();
        let index = 0;
        while (index < set.length - 1 && new Date(set[index].datetime).getTime() <= targetDate) {
          index++;
        }
        if (index < set.length) {
          firstDateTime = new Date(set[index].datetime).getTime();
        }
      }
      if(set.length > 0 && (firstDateTime < domainMin)) {
        domainMin = firstDateTime;
      }
    });
    if (domainMin === Number.MAX_VALUE) return "auto";
    return domainMin;
  }

  // Return the maximum datetime as an integer in milliseconds in the domain of a dataset
function getDomainMax(newHeatData) {
  const targetDate = new Date("2021-10-01").getTime(); // Maximum allowed date: 1-10-21
  let domainMax = 0;
  
  newHeatData.forEach(set => {
    if (set.length > 0) {
      let index = set.length - 1;
      while (index >= 0 && new Date(set[index].datetime).getTime() >= targetDate) {
        index--;
      }
      if (index >= 0) {
        const currentDateTime = new Date(set[index].datetime).getTime();
        if (currentDateTime > domainMax) {
          domainMax = currentDateTime;
        }
      }
    }
  });

  if (domainMax === 0) return "auto";
  return domainMax;
}

  // GET the metadata from the backend
  async function getMetaData() {
    const metaData = await api.get("./metadata");
    setMetaData(metaData.data);
  }

  // GET the heat data of a single measuring device from the backend
  async function getHeatData(deviceId, dataSetIdx) {
    const newDataSet = await api.get(`./heatdata/${deviceId}`);
    const newHeatData = heatData;
    newHeatData[dataSetIdx] = newDataSet.data;
    setHeatData(newHeatData);
    const domainMin = getDomainMin(newHeatData);
    setSelectedDay(new Date(domainMin));
    setDataSetsDomain([domainMin,getDomainMax(newHeatData)]);
  }

  // When this component is rendered, obtain the metadata
  useEffect(() => {
    getMetaData();
  }, [metaData.length]);

  function refreshPage() {
    window.location.reload(false);
  }

  // The component is a grid containing 5 boxes with components, along with a sidebar (Offcanvas) that is not always shown
  return (
    <div>
      <div className={"four-six-grid"}>
        <div style={{ gridArea: "im" }} >
          <h1 onClick={() => openSideBar("importingStage")}> <i className="fa fa-file-text main-color"></i></h1>
          <ImportingStage />
        </div>
        <div style={{ gridArea: "fi" }} >
          <h1 onClick={() => openSideBar("filteringStage")} style={{width: "65%"}}><i className="fa fa-filter main-color"></i></h1>
          <FilterStage metaData={metaData} setHeatData={setHeatData} getHeatData={getHeatData} heatData={heatData} theme={theme}/>
        </div>
        <div style={{ gridArea: "ma" }} >
          <h1 onClick={() => openSideBar("mappingStage")}><i className="fa fa-calendar main-color"></i></h1>
          <MappingStage heatData={heatData} dataSetsDomain={dataSetsDomain} setStrokeWidth={setStrokeWidth} domain={domain} setDomain={setDomain} 
                        selectedDay={selectedDay} setSelectedDay={setSelectedDay} chartType={chartType} setChartType={setChartType} theme={theme}/>
        </div>
        <div style={{ gridArea: "re", minWidth: "700px"}} >
          <h1 onClick={() => openSideBar("renderingStage")}><i className="fa fa-line-chart main-color"></i></h1>
          <RenderingStage selectedDay={selectedDay} dataSetsDomain={dataSetsDomain} strokeWidth={strokeWidth} heatData={heatData} domain={domain} chartType={chartType}/>
        </div>
        <div style={{ gridArea: "in"}} >
          <h1 onClick={() => openSideBar("insightStage")}><i className="fa fa-search main-color"></i></h1>
          <InsightStage />
        </div>
        <Offcanvas show={showSideBar} onHide={closeSideBar}>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>{ sideBarTitle }</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body style={{ whiteSpace: "pre-line" }}>{ sideBarBody }</Offcanvas.Body>
        </Offcanvas>
      </div>
      <Button onClick={ refreshPage }>
        Reset
      </Button>
    </div>
  );
}
